import { useState, useMemo, useEffect } from "react";
import { useQuery, useLazyQuery, useMutation } from "@apollo/client/react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";

function useBusySet() {
  const [keys, setKeys] = useState<Set<string>>(new Set());
  return useMemo(() => ({
    has: (key: string) => keys.has(key),
    size: keys.size,
    add: (key: string) => setKeys((prev) => new Set(prev).add(key)),
    delete: (key: string) => setKeys((prev) => { const next = new Set(prev); next.delete(key); return next; }),
  }), [keys]);
}
import {
  Loader2,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  BookOpen,
  Calculator,
  FileText,
  Plus,
  CheckCircle2,
  Eye,
  Check,
  X,
  Trash2,
  Pencil,
  Wand2,
} from "lucide-react";
import { toast } from "sonner";

import {
  ContentFilterBar,
  UNTAGGED,
  filterSkills,
  matchesConcepts,
  useContentCatalog,
  useContentFilter,
  type ContentFilter,
} from "@/components/admin/content-filters";
import {
  GET_EXERCISES_SUMMARY,
  GET_ALL_EXERCISES,
  GET_PENDING_REVIEW_COUNTS,
  GET_PENDING_EXERCISES,
  GET_PENDING_EXERCISE_PATHS,
  GET_PENDING_EXERCISE_COUNTS,
} from "@/graphql/queries/admin";
import {
  APPROVE_EXERCISE_FILES,
  REJECT_EXERCISE_FILES,
  GENERATE_SUGGESTED_EXERCISES,
  GENERATE_EXERCISE_FILES,
} from "@/graphql/mutations/admin";
import { MathContent } from "@/components/math/math-content";
import {
  Card,
  CardContent,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { ExerciseEditDialog } from "@/components/admin/ExerciseEditDialog";

interface ExerciseQuestion {
  html: string;
  answer: string;
  hint?: string;
}

interface Exercise {
  key: string;
  title: string;
  question: ExerciseQuestion;
  metadata: { skillKey: string; level: number; exerciseKey: string };
  /** Concept keys this question teaches. Empty means untagged. */
  concepts: string[];
}

/** One thing the skill's theory teaches, with how many questions practise it. */
interface SkillConcept {
  key: string;
  name: string;
  description?: string | null;
  exerciseCount: number;
  /**
   * Theory blocks tagged with it. Zero: the questions exist, the explanation
   * doesn't. Null: the theory carries no tags at all — nothing is claimed either
   * way, which is every theory file written before tagging.
   */
  theoryBlocks: number | null;
  /**
   * Questions this concept wants AT THIS LEVEL, from its own `levels` and
   * `priority` (ConceptTarget). Zero means the level is not for this concept —
   * a different statement from being short, and shown differently.
   */
  target: number;
  levels: number[];
  priority: "high" | "medium" | "low";
  /** False when nobody decided levels/priority yet, so a fallback reads as one. */
  difficultyDeclared: boolean;
  /** False when nothing has counted the questions: the count is unknown, not zero. */
  coverageMeasured: boolean;
}

interface LevelData {
  level: number;
  exercises: Exercise[];
  concepts: SkillConcept[];
}

interface SkillData {
  key: string;
  name: string;
  grade: number;
  levels: LevelData[];
}

interface SkillInfo {
  key: string;
  name: string;
  counts: { level_1: number; level_2: number; level_3: number };
}

interface Summary {
  [subject: string]: {
    name: string;
    grades: Record<number, { skills: SkillInfo[] }>;
  };
}

const subjectIcons: Record<string, React.ReactNode> = {
  math: <Calculator className="h-5 w-5" />,
  lithuanian: <BookOpen className="h-5 w-5" />,
};

const levelColors: Record<number, string> = {
  1: "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200",
  2: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200",
  3: "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200",
};

// How many questions a concept wants now comes from the server, per concept and
// per level (ConceptTarget), because a flat number asked for twenty hard
// questions about recognising a circle. This is only the label for the priority
// that scales it.
const priorityLabels: Record<string, string> = {
  high: "svarbi",
  medium: "įprasta",
  low: "papildoma",
};

const levelNames: Record<number, string> = {
  1: "Lengvas",
  2: "Vidutinis",
  3: "Sunkus",
};

// Build file path from exercise key like "math/grade_1/g1-clock-hours/level_1/001/001" (variant)
// or "math/grade_1/g1-clock-hours/level_1/001" (flat)
function exerciseKeyToFilePath(exerciseFullKey: string): string {
  const parts = exerciseFullKey.split("/");
  if (parts.length >= 6) {
    const [subject, grade, skill, level, exercise, variant] = parts;
    return `${subject}/${grade}/${skill}/${level}/exercise_${exercise}/variant_${variant}.json`;
  }
  if (parts.length >= 5) {
    const [subject, grade, skill, level, exercise] = parts;
    return `${subject}/${grade}/${skill}/${level}/exercise_${exercise}.json`;
  }
  return exerciseFullKey;
}

// ─── Pending Exercises View (files in content/exercises-pending/) ───
// Mirrors the review step in ExerciseGeneratePage

interface PendingExercise {
  file_path: string;
  skill_key: string;
  /** Translated skill name. The key is kept alongside it: paths, config and every
   *  log line use the key, so a reviewer needs it to find anything. */
  skill_name: string | null;
  level: number;
  subject: string;
  grade: number;
  exercise_type: string;
  title: string | null;
  problem_html: string | null;
  answer: string | null;
  choices: string[] | null;
  hint: string | null;
  /** What this question practises, from its own tag. Empty for anything generated
   *  before the skill declared a vocabulary. */
  concepts: { key: string; name: string }[] | null;
}

/** How the queue is ordered. Level is the default because a reviewer works down
 *  a level at a time; concept is what you want when checking whether a batch
 *  generated for one concept actually landed on it. */
type PendingSort = "level" | "concept";

/** One skill in the review queue, for the navigator above the page. */
interface PendingSkill {
  key: string;
  name: string | null;
  count: number;
  /** Which page it is on. A page is whole skills — see GET_PENDING_EXERCISES. */
  page: number;
}

function PendingExercisesView({
  filter, setFilter, sort, onSortChange, atSkill, onAtSkillChange,
  onPageSkillsChange, onCountChange,
}: {
  /** The page's filters, shared with the approved view — see ContentFilterBar. */
  filter: ContentFilter;
  setFilter: (patch: Partial<ContentFilter>) => void;
  sort?: PendingSort;
  onSortChange: (sort: string) => void;
  /** Which skill's page to open, from the URL — see `atSkill` on the resolver.
   *  The queue is navigated by skill, and a skill key is what survives being
   *  pasted into a message: page numbers shift as skills are approved away. */
  atSkill?: string;
  onAtSkillChange: (skillKey: string) => void;
  /** The skills on screen, so the page can scope its concept picker to them. */
  onPageSkillsChange?: (skillKeys: string[]) => void;
  onCountChange?: () => void;
}) {
  const sortBy: PendingSort = sort === "concept" ? "concept" : "level";

  // Every filter is a list, and an empty list means "no filter" — never "match
  // nothing". The server reads them the same way, so the two cannot disagree.
  const filterVars = useMemo(
    () => ({
      subjects: filter.subjects,
      grades: filter.grades,
      skills: filter.skills,
      levels: filter.levels,
      concepts: filter.concepts,
    }),
    [filter],
  );

  // Nothing resets the page when the filters change: the server falls back to the
  // first page for a skill the new filters exclude, and keeps the reviewer where
  // they are when it survives — which is what narrowing by level or concept while
  // reading a skill is for. Clearing it here would jump them to the front of the
  // queue for adding a filter to the skill in front of them.
  const { data, loading, refetch } = useQuery(GET_PENDING_EXERCISES, {
    fetchPolicy: "network-only",
    variables: { ...filterVars, sort: sortBy, atSkill: atSkill || undefined },
  });

  // What „Ištrinti visą sąrašą" acts on: the whole filtered queue, which is the
  // one action reaching past the skill on screen. Fetched only when it is
  // pressed — half a megabyte of paths on every page load would be several times
  // the page's own content.
  const [fetchAllPaths] = useLazyQuery(GET_PENDING_EXERCISE_PATHS, {
    fetchPolicy: "network-only",
  });

  const [approveExerciseFiles] = useMutation(APPROVE_EXERCISE_FILES);
  const [rejectExerciseFiles] = useMutation(REJECT_EXERCISE_FILES);

  // Edit dialog state
  const [editDialog, setEditDialog] = useState<{
    open: boolean;
    filePath: string | null;
    data: PendingExercise | null;
  }>({ open: false, filePath: null, data: null });

  // Delete confirmation dialog state
  const [deleteConfirm, setDeleteConfirm] = useState<{ open: boolean; filePaths: string[]; label: string }>({ open: false, filePaths: [], label: "" });

  // Tracks approved and deleted paths locally (no refetch)
  // approvedPaths: visually fading out; hiddenPaths: fully removed from list
  const [approvedPaths, setApprovedPaths] = useState<Set<string>>(new Set());
  const [hiddenPaths, setHiddenPaths] = useState<Set<string>>(new Set());
  const [removedPaths, setRemovedPaths] = useState<Set<string>>(new Set());
  const busyPaths = useBusySet();

  const payload = data?.pendingExercises;
  const parsed: {
    exercises?: PendingExercise[];
    filtered_total?: number;
    page?: number;
    page_count?: number;
    skills?: PendingSkill[];
  } = payload
    ? typeof payload === "string"
      ? JSON.parse(payload)
      : payload
    : {};

  const pageExercises: PendingExercise[] = parsed.exercises || [];
  const filteredTotal = parsed.filtered_total ?? 0;
  const pageCount = Math.max(1, parsed.page_count ?? 1);
  const skillIndex: PendingSkill[] = parsed.skills || [];
  // The server resolves the skill in the URL to a page and clamps one past the
  // end, so this is what the page actually is rather than what was asked for.
  const currentPage = parsed.page ?? 0;

  // Filter out deleted and fully-hidden (post-fade) exercises. Only this page's
  // own rows: the rest of the queue is on the server.
  const exercises = pageExercises.filter(
    (e) => !removedPaths.has(e.file_path) && !hiddenPaths.has(e.file_path),
  );

  // Group by skill. A page is whole skills, so every group here is that skill's
  // entire pending set — which is what makes „Patvirtinti visus" on it honest.
  const grouped: Record<string, PendingExercise[]> = {};
  for (const ex of exercises) {
    const key = ex.skill_key || "unknown";
    if (!grouped[key]) grouped[key] = [];
    grouped[key].push(ex);
  }
  const skillKeys = Object.keys(grouped);

  /**
   * A concept badge is a filter. Picking one selects its skill too: the concept
   * picker above offers the concepts of the *selected* skills, so a concept
   * chosen down here would otherwise be a filter with no visible source.
   * „Be sąvokos" takes no skill with it — it is a question about the whole queue.
   */
  const toggleConcept = (key: string, skillKey?: string) => {
    const on = filter.concepts.includes(key);
    setFilter({
      concepts: on ? filter.concepts.filter((k) => k !== key) : [...filter.concepts, key],
      skills:
        on || !skillKey || filter.skills.includes(skillKey)
          ? filter.skills
          : [...filter.skills, skillKey],
    });
  };

  /**
   * Every draft in the filtered queue — what „Ištrinti visą sąrašą" means, and
   * the only action that reaches past the skill on screen. The per-skill buttons
   * do not use it: a page is that skill whole, so they act on what is displayed.
   */
  const allFilteredPaths = async (): Promise<string[]> => {
    try {
      const result = await fetchAllPaths({ variables: filterVars });
      if (result.error) throw result.error;
      const paths: string[] = result.data?.pendingExercisePaths || [];
      const usable = paths.filter((fp) => !removedPaths.has(fp) && !hiddenPaths.has(fp));
      if (usable.length === 0) toast.error("Nepavyko gauti užduočių sąrašo");
      return usable;
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : "Nepavyko gauti užduočių sąrašo");
      return [];
    }
  };

  /**
   * On to the next skill, or a plain refetch when this was the last one. Called
   * once a bulk action has emptied the page in front of the reviewer.
   */
  const advancePage = () => {
    const next = pageOptions.find((o) => o.page === currentPage + 1);
    if (next) onAtSkillChange(next.key);
    else refetch();
  };

  const handleApprove = async (filePaths: string[], andRefetch = false) => {
    const toApprove = filePaths.filter((fp) => !approvedPaths.has(fp) && !busyPaths.has(fp));
    if (toApprove.length === 0) return;
    for (const fp of toApprove) busyPaths.add(fp);
    try {
      const { data } = await approveExerciseFiles({ variables: { filePaths: toApprove } });
      if (data?.approveExerciseFiles?.success) {
        setApprovedPaths((prev) => {
          const next = new Set(prev);
          toApprove.forEach((fp) => next.add(fp));
          return next;
        });
        // After fade animation, move to hidden so they leave the list
        setTimeout(() => {
          setHiddenPaths((prev) => {
            const next = new Set(prev);
            toApprove.forEach((fp) => next.add(fp));
            return next;
          });
          // A bulk action empties the page. Moving on to the next skill is what
          // the reviewer meant by clearing this one — falling back to the first
          // page, which is what a now-missing skill resolves to, would send them
          // back to the start of the queue.
          if (andRefetch) advancePage();
        }, 500);
        onCountChange?.();
      } else {
        toast.error(data?.approveExerciseFiles?.errors?.join(", ") || "Klaida");
      }
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : "Klaida");
    } finally {
      for (const fp of toApprove) busyPaths.delete(fp);
    }
  };

  const handleDelete = async (filePaths: string[], andRefetch = false) => {
    const toDelete = filePaths.filter((fp) => !busyPaths.has(fp));
    if (toDelete.length === 0) return;
    for (const fp of toDelete) busyPaths.add(fp);
    try {
      const { data } = await rejectExerciseFiles({ variables: { filePaths: toDelete } });
      if (data?.rejectExerciseFiles?.success) {
        setRemovedPaths((prev) => {
          const next = new Set(prev);
          toDelete.forEach((fp) => next.add(fp));
          return next;
        });
        onCountChange?.();
        if (andRefetch) advancePage();
      } else {
        toast.error(data?.rejectExerciseFiles?.errors?.join(", ") || "Klaida");
      }
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : "Klaida");
    } finally {
      for (const fp of toDelete) busyPaths.delete(fp);
    }
  };

  const confirmDeleteEverything = async () => {
    const paths = await allFilteredPaths();
    if (paths.length) setDeleteConfirm({ open: true, filePaths: paths, label: "" });
  };

  const hasFilters =
    filter.subjects.length + filter.grades.length + filter.skills.length +
    filter.levels.length + filter.concepts.length > 0;

  // Only what belongs to this view: the subject/grade/skill/level/concept pickers
  // live on the page, above both views, so a filter set while reviewing drafts
  // still holds when switching to the approved questions.
  const subject = exercises[0]?.subject;
  // The skill on screen, not the one in the filter: with the „Įgūdis" picker off
  // in this view, the page itself is what names a skill, and „Generuoti
  // užduotis" is about the one being read.
  const oneSkill = skillKeys.length === 1 ? skillKeys[0] : undefined;

  // One entry per page, named by the skill it holds and addressed by that skill's
  // key. A page is whole skills, so the name is what the reviewer is about to
  // read — „7 iš 29" is not — and the key is what a shared link carries.
  const pageOptions = useMemo(() => {
    const byPage = new Map<number, PendingSkill[]>();
    for (const skill of skillIndex) {
      const list = byPage.get(skill.page) || [];
      list.push(skill);
      byPage.set(skill.page, list);
    }
    return [...byPage.entries()]
      .sort((a, b) => a[0] - b[0])
      .map(([pageIndex, entries]) => {
        const total = entries.reduce((sum, e) => sum + e.count, 0);
        const name =
          entries.length === 1
            ? entries[0].name || entries[0].key
            : `${entries.length} įgūdžiai`;
        return {
          page: pageIndex,
          key: entries[0].key,
          label: `${pageIndex + 1}. ${name} (${total})`,
        };
      });
  }, [skillIndex]);

  /** Moving pages is naming a skill, because that is what the URL carries. */
  const goToPage = (target: number) => {
    const option = pageOptions.find((o) => o.page === target);
    if (option) onAtSkillChange(option.key);
  };

  // Joined rather than passed as an array: the list is rebuilt every render, and
  // an effect keyed on its identity would fire on every one of them.
  const pageSkillKeys = skillKeys.join(",");
  useEffect(() => {
    onPageSkillsChange?.(pageSkillKeys ? pageSkillKeys.split(",") : []);
  }, [pageSkillKeys, onPageSkillsChange]);

  const pager = pageCount > 1 ? (
    <div className="flex items-center gap-2">
      <Button
        variant="outline"
        size="sm"
        className="h-8 text-xs"
        disabled={currentPage === 0 || loading}
        onClick={() => goToPage(currentPage - 1)}
      >
        <ChevronLeft className="h-3.5 w-3.5" />
        Ankstesnis
      </Button>
      <Select
        value={pageOptions.find((o) => o.page === currentPage)?.key ?? ""}
        onValueChange={onAtSkillChange}
      >
        <SelectTrigger className="h-8 text-sm w-[280px]">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {pageOptions.map((option) => (
            <SelectItem key={option.key} value={option.key}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Button
        variant="outline"
        size="sm"
        className="h-8 text-xs"
        disabled={currentPage + 1 >= pageCount || loading}
        onClick={() => goToPage(currentPage + 1)}
      >
        Kitas
        <ChevronRight className="h-3.5 w-3.5" />
      </Button>
    </div>
  ) : null;

  const filterBar = (
    <div className="flex gap-3 items-center flex-wrap">
      <Select value={sortBy} onValueChange={onSortChange}>
        <SelectTrigger className="w-[190px] h-8 text-sm">
          <SelectValue placeholder="Rikiuoti" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="level">Rikiuoti pagal lygį</SelectItem>
          <SelectItem value="concept">Rikiuoti pagal sąvoką</SelectItem>
        </SelectContent>
      </Select>

      <span className="text-sm text-muted-foreground ml-auto tabular-nums">
        {exercises.length} iš {filteredTotal} užd.
      </span>
      {pager}
      {oneSkill && subject && (
        <Button variant="outline" size="sm" className="h-8 text-xs" asChild>
          <Link to={`/admin/exercises/generate?${new URLSearchParams({
            skill: oneSkill,
            subject,
            grade: String(exercises[0]?.grade ?? ""),
            ...(filter.levels.length === 1 ? { level: String(filter.levels[0]) } : {}),
          })}`}>
            <Plus className="h-3.5 w-3.5 mr-1" />
            Generuoti užduotis
          </Link>
        </Button>
      )}
    </div>
  );

  if (loading && exercises.length === 0) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        <span className="ml-2 text-sm text-muted-foreground">
          Kraunamos laukiančios užduotys...
        </span>
      </div>
    );
  }

  if (exercises.length === 0) {
    return (
      <div className="space-y-4 max-w-6xl">
        {filteredTotal > 0 || hasFilters ? filterBar : null}
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            <CheckCircle2 className="h-8 w-8 mx-auto mb-2 text-green-500" />
            {hasFilters ? "Nėra užduočių pagal pasirinktus filtrus" : "Nėra laukiančių patvirtinimo"}
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-4 max-w-6xl">
      {filterBar}
      {skillKeys.map((sk) => {
        const skillExercises = grouped[sk];
        const pendingCount = skillExercises.filter((e) => !approvedPaths.has(e.file_path)).length;

        return (
          <Card key={sk}>
            <CardContent className="pt-4 pb-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-medium flex items-baseline gap-2 min-w-0">
                  <span className="truncate">{skillExercises[0]?.skill_name || sk}</span>
                  <span className="text-xs text-muted-foreground font-mono shrink-0">{sk}</span>
                  <Badge className="text-xs shrink-0 bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200">
                    {pendingCount}
                  </Badge>
                </h3>
                <div className="flex items-center gap-1">
                  {/* A page is this skill whole, so „visus" is exactly the
                      questions on screen — no hidden remainder, and no second
                      request to find out what the button is about to do. */}
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleApprove(skillExercises.map((e) => e.file_path), true)}
                    className="text-xs h-7 text-green-600 hover:text-green-700"
                  >
                    <CheckCircle2 className="h-3.5 w-3.5 mr-1" />
                    Patvirtinti visus
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setDeleteConfirm({ open: true, filePaths: skillExercises.map((e) => e.file_path), label: sk })}
                    className="text-xs h-7 text-red-500 hover:text-red-600"
                  >
                    <Trash2 className="h-3.5 w-3.5 mr-1" />
                    Ištrinti visus
                  </Button>
                </div>
              </div>
              <div className="space-y-3">
                {skillExercises.map((exercise, i) => {
                  const isApproved = approvedPaths.has(exercise.file_path);
                  const isBusy = busyPaths.has(exercise.file_path);
                  return (
                    <div
                      key={exercise.file_path}
                      className={`border rounded-lg p-3 space-y-2 transition-all duration-500 ${
                        isApproved
                          ? "bg-green-50 border-green-200 dark:bg-green-950/20 dark:border-green-800 opacity-0 scale-y-95 max-h-0 !p-0 !m-0 overflow-hidden"
                          : "bg-background opacity-100 max-h-[2000px]"
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <button
                          disabled={isBusy || isApproved}
                          onClick={() => handleApprove([exercise.file_path])}
                          className={`shrink-0 h-6 w-6 rounded-full flex items-center justify-center border-2 transition-colors ${
                            isApproved
                              ? "bg-green-500 border-green-500 text-white"
                              : "border-muted-foreground/30 hover:border-green-400 hover:bg-green-50 dark:hover:bg-green-950"
                          }`}
                          title="Patvirtinti"
                        >
                          {isBusy ? (
                            <Loader2 className="h-3 w-3 animate-spin" />
                          ) : isApproved ? (
                            <Check className="h-3.5 w-3.5" />
                          ) : null}
                        </button>
                        <Badge variant="outline" className="text-xs shrink-0">
                          {i + 1}
                        </Badge>
                        <Badge className={`text-xs shrink-0 ${levelColors[exercise.level] || ""}`}>
                          L{exercise.level}
                        </Badge>
                        {/* What the question practises. A reviewer approving 1,300
                            questions needs to see whether the batch is about the
                            concept it was generated for — and clicking narrows the
                            queue to just that concept, which is how you check it. */}
                        {exercise.concepts?.length ? (
                          exercise.concepts.map((c) => {
                            const on = filter.concepts.includes(c.key);
                            return (
                              <button
                                key={c.key}
                                onClick={() => toggleConcept(c.key, exercise.skill_key)}
                                title={on ? `${c.key} — spustelėk, kad atfiltruotum` : `${c.key} — rodyti tik šią sąvoką`}
                              >
                                <Badge
                                  variant={on ? "default" : "secondary"}
                                  className="text-xs shrink-0 font-normal cursor-pointer hover:opacity-80"
                                >
                                  {c.name}
                                </Badge>
                              </button>
                            );
                          })
                        ) : (
                          <button
                            onClick={() => toggleConcept(UNTAGGED)}
                            title={filter.concepts.includes(UNTAGGED) ? "Spustelėk, kad atfiltruotum" : "Rodyti tik užduotis be sąvokos"}
                          >
                            <Badge
                              variant={filter.concepts.includes(UNTAGGED) ? "default" : "outline"}
                              className="text-xs shrink-0 font-normal cursor-pointer hover:opacity-80 text-muted-foreground"
                            >
                              Be sąvokos
                            </Badge>
                          </button>
                        )}
                        <span className="ml-auto flex items-center gap-2">
                          <span className="text-xs text-muted-foreground font-mono truncate max-w-[300px]">
                            {exercise.file_path}
                          </span>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-6 w-6 p-0 text-muted-foreground hover:text-foreground shrink-0"
                            disabled={isBusy || isApproved}
                            onClick={(e) => { e.stopPropagation(); setEditDialog({ open: true, filePath: exercise.file_path, data: exercise }); }}
                            title="Redaguoti"
                          >
                            <Pencil className="h-3.5 w-3.5" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-6 w-6 p-0 text-red-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-950 shrink-0"
                            disabled={isBusy || isApproved}
                            onClick={() => handleDelete([exercise.file_path])}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </span>
                      </div>
                      {exercise.problem_html && (
                        <MathContent
                          html={exercise.problem_html}
                          className="exercise-content text-sm border rounded p-3 bg-muted/30 [&_p]:mb-1 [&_svg]:inline-block [&_svg]:max-w-full [&_img]:max-w-xs [&_img]:rounded"
                        />
                      )}
                      <div className="flex items-center gap-4 text-xs text-muted-foreground flex-wrap">
                        {exercise.answer && (
                          <span>
                            <strong>Atsakymas:</strong>{" "}
                            <MathContent html={exercise.answer} className="inline bg-muted px-1 py-0.5 rounded" />
                          </span>
                        )}
                        {exercise.choices && exercise.choices.length > 0 && (
                          <span className="flex items-center gap-1 flex-wrap">
                            <strong>Pasirinkimai:</strong>{" "}
                            {exercise.choices.map((c: string, ci: number) => (
                              <MathContent key={ci} html={c} className="inline bg-muted px-1 py-0.5 rounded" />
                            ))}
                          </span>
                        )}
                        {exercise.hint && (
                          <span>
                            <strong>Užuomina:</strong> <MathContent html={exercise.hint} className="inline" />
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        );
      })}

      {/* Bottom actions bar */}
      <div className="flex items-center justify-between sticky bottom-0 bg-background py-3 border-t gap-3 flex-wrap">
        <span className="text-sm text-muted-foreground tabular-nums">
          {exercises.length} iš {filteredTotal} užd.
        </span>
        {pager}
        <div className="flex items-center gap-2">
          {/* This page is whole skills, so the two buttons above each card are
              the only bulk actions a reviewer needs — everything they cover is on
              screen. What is left down here is the one action that reaches past
              it, and it says so and carries its count. */}
          <Button
            variant="ghost"
            size="sm"
            onClick={confirmDeleteEverything}
            className="text-xs text-red-500 hover:text-red-600"
          >
            <Trash2 className="h-3.5 w-3.5 mr-1" />
            Ištrinti visą sąrašą ({filteredTotal})
          </Button>
        </div>
      </div>

      <ExerciseEditDialog
        filePath={editDialog.filePath}
        initialData={editDialog.data ? {
          question: editDialog.data.problem_html || "",
          answer: editDialog.data.answer || "",
          hint: editDialog.data.hint || undefined,
          choices: editDialog.data.choices || undefined,
        } : null}
        open={editDialog.open}
        onOpenChange={(open) => setEditDialog((prev) => ({ ...prev, open }))}
        onSaved={() => {
          // Refetch to pick up the edited content
          refetch();
        }}
      />

      <AlertDialog open={deleteConfirm.open} onOpenChange={(open) => setDeleteConfirm((prev) => ({ ...prev, open }))}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Ištrinti užduotis?</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteConfirm.label
                ? `Ar tikrai norite ištrinti visas "${deleteConfirm.label}" užduotis (${deleteConfirm.filePaths.length})?`
                : `Ar tikrai norite ištrinti visas ${deleteConfirm.filePaths.length} užduotis?`}
              {" "}Šio veiksmo negalima atšaukti.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Atšaukti</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700 text-white"
              onClick={() => handleDelete(deleteConfirm.filePaths, true)}
            >
              <Trash2 className="h-4 w-4 mr-1" />
              Ištrinti
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// ─── Lazy-loaded exercises for a single skill ───

/**
 * One question, with the concepts it teaches.
 *
 * `number` is its position in the level, kept the same in both views: a question
 * discussed as "#12" has to still be #12 after grouping, or the two views name
 * different things.
 */
function ExerciseRow({
  exercise,
  number,
  concepts,
}: {
  exercise: Exercise;
  number?: number;
  concepts: SkillConcept[];
}) {
  const filePath = exerciseKeyToFilePath(exercise.key);

  return (
    <div className="px-4 py-3 space-y-2">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 flex-wrap">
          <h3 className="text-sm font-medium">#{number ?? "?"}</h3>
          {exercise.concepts.length > 0 ? (
            exercise.concepts.map((key) => (
              <Badge key={key} variant="secondary" className="text-[10px]">
                {concepts.find((c) => c.key === key)?.name || key}
              </Badge>
            ))
          ) : concepts.length > 0 ? (
            <Badge
              variant="outline"
              className="text-[10px] text-amber-600 dark:text-amber-400 border-amber-400/50"
              title="Ši užduotis netinka nė vienai teorijos sąvokai — teorija jos neapima"
            >
              be sąvokos
            </Badge>
          ) : null}
        </div>
        <div className="flex items-center gap-2">
          <span
            className="text-[10px] text-muted-foreground font-mono select-all cursor-text"
            onClick={(e) => e.stopPropagation()}
          >
            {filePath}
          </span>
        </div>
      </div>
      <div className="space-y-1.5">
        <div className="border-l-2 border-primary/30 pl-3 py-1">
          <MathContent html={exercise.question.html} className="exercise-content text-sm" />
          <div className="flex gap-4 mt-1 text-xs">
            <span className="text-green-600 dark:text-green-400">
              <strong>Ats:</strong> <MathContent html={exercise.question.answer} className="inline" />
            </span>
            {exercise.question.hint && (
              <span className="text-orange-600 dark:text-orange-400">
                <strong>Užuomina:</strong>{" "}
                <MathContent html={exercise.question.hint} className="inline" />
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/** One tag's worth of questions, in the vocabulary's order. */
interface ConceptGroup {
  key: string;
  name: string;
  description?: string | null;
  theoryBlocks: number | null;
  exercises: Exercise[];
}

/**
 * The level's questions grouped by the concept they teach.
 *
 * A question carrying two tags appears under both — it really does practise both,
 * and hiding it from one of them would make that concept look thinner than it is.
 * Concepts with no questions are left out here: the coverage bar above already
 * names them, and an empty heading says less than a zero next to the name.
 *
 * The last group is the questions no concept claims. That one is the interesting
 * one — it is the level's work that the theory does not cover.
 */
function groupByConcept(level: LevelData): ConceptGroup[] {
  const groups: ConceptGroup[] = level.concepts
    .map((concept) => ({
      key: concept.key,
      name: concept.name,
      description: concept.description,
      theoryBlocks: concept.theoryBlocks,
      exercises: level.exercises.filter((e) => e.concepts.includes(concept.key)),
    }))
    .filter((group) => group.exercises.length > 0);

  // Tags on questions whose key left the vocabulary (a renamed concept) would
  // otherwise vanish from the page while still counting nowhere.
  const known = new Set(level.concepts.map((c) => c.key));
  const strays = new Map<string, Exercise[]>();
  for (const exercise of level.exercises) {
    for (const key of exercise.concepts) {
      if (known.has(key)) continue;
      strays.set(key, [...(strays.get(key) || []), exercise]);
    }
  }
  for (const [key, exercises] of strays) {
    groups.push({ key, name: key, description: "Šios sąvokos nebėra teorijos sąraše", theoryBlocks: 0, exercises });
  }

  const untagged = level.exercises.filter((e) => e.concepts.length === 0);
  if (untagged.length > 0) {
    groups.push({
      key: "__untagged__",
      name: "Be sąvokos",
      description: "Šių užduočių teorija neapima — arba papildyk teoriją, arba perkelk jas",
      theoryBlocks: null,
      exercises: untagged,
    });
  }

  return groups;
}

function SkillExercises({
  subject,
  grade,
  skillKey,
  filter,
}: {
  subject: string;
  grade: number;
  skillKey: string;
  /** The page's filters. Levels and concepts narrow what is shown inside a skill;
   *  subject, grade and skill decided that this skill is on screen at all. */
  filter: ContentFilter;
}) {
  const { data, loading } = useQuery(GET_ALL_EXERCISES, {
    variables: { subject, grade, skill: skillKey },
  });
  // Grouped by default wherever there are tags: a flat list of fifty questions is
  // what made a missing concept invisible in the first place.
  const [flatList, setFlatList] = useState(false);

  // level => (exercise key => its number in the level), so grouping renumbers
  // nothing.
  const numbering = useMemo(() => {
    const levels: LevelData[] = data?.allExercises?.skills?.[0]?.levels || [];
    return new Map(
      levels.map((level) => [
        level.level,
        new Map(level.exercises.map((exercise, idx) => [exercise.key, idx + 1])),
      ])
    );
  }, [data]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8 border border-t-0 rounded-b-lg">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        <span className="ml-2 text-sm text-muted-foreground">
          Kraunamos užduotys...
        </span>
      </div>
    );
  }

  const skill: SkillData | undefined = data?.allExercises?.skills?.[0];
  if (!skill) {
    return (
      <div className="px-4 py-6 text-center text-sm text-muted-foreground border border-t-0 rounded-b-lg">
        Nerasta užduočių
      </div>
    );
  }

  // The numbering above is the level's own and is deliberately computed before
  // this: „#12" has to mean the same question whether or not a concept filter is
  // on, exactly as it means the same in the grouped and the flat view.
  const levels = skill.levels
    .filter((level) => filter.levels.length === 0 || filter.levels.includes(level.level))
    .map((level) => ({
      ...level,
      exercises: level.exercises.filter((exercise) =>
        matchesConcepts(filter, exercise.concepts),
      ),
    }));

  if (levels.every((level) => level.exercises.length === 0)) {
    return (
      <div className="px-4 py-6 text-center text-sm text-muted-foreground border border-t-0 rounded-b-lg">
        Nėra užduočių pagal pasirinktus filtrus
      </div>
    );
  }

  return (
    <div className="border border-t-0 rounded-b-lg">
      {levels.map((levelData) => {
        if (levelData.exercises.length === 0) return null;

        return (
          <div key={levelData.level}>
            <div className="bg-muted/30 px-4 py-2 border-t flex items-center gap-2">
              <Badge
                className={`text-xs ${levelColors[levelData.level]}`}
              >
                L{levelData.level}
              </Badge>
              <span className="text-sm font-medium">
                {levelNames[levelData.level]}
              </span>
              <span className="text-xs text-muted-foreground">
                ({levelData.exercises.length})
              </span>
              {levelData.concepts.length > 0 && (
                <button
                  type="button"
                  onClick={() => setFlatList((prev) => !prev)}
                  className="ml-auto text-xs text-muted-foreground hover:text-foreground cursor-pointer"
                >
                  {flatList ? "Grupuoti pagal sąvokas" : "Rodyti sąrašu"}
                </button>
              )}
            </div>

            {/* What the theory teaches, and how much of it this level actually
                practises. A zero is the useful number: the theory covers the
                idea and nothing tests it. */}
            {levelData.concepts.length > 0 && (
              <ConceptCoverageBar
                subject={subject}
                grade={grade}
                skillKey={skillKey}
                level={levelData.level}
                concepts={levelData.concepts}
                untaggedCount={
                  levelData.exercises.filter((e) => e.concepts.length === 0).length
                }
              />
            )}

            {levelData.concepts.length > 0 && !flatList ? (
              groupByConcept(levelData).map((group) => (
                <div key={group.key}>
                  <div
                    className="px-4 py-1.5 border-t bg-muted/10 flex items-center gap-2 text-xs sticky top-0"
                    title={group.description || undefined}
                  >
                    <span
                      className={
                        group.key === "__untagged__"
                          ? "font-medium text-amber-700 dark:text-amber-400"
                          : "font-medium"
                      }
                    >
                      {group.name}
                    </span>
                    <span className="font-mono text-muted-foreground">
                      {group.exercises.length}
                    </span>
                    {group.theoryBlocks === 0 && (
                      <span
                        className="text-amber-600 dark:text-amber-400"
                        title="Užduočių yra, o teorijoje šios sąvokos niekas nepaaiškina"
                      >
                        nėra teorijoje
                      </span>
                    )}
                  </div>
                  <div className="divide-y">
                    {group.exercises.map((exercise) => (
                      <ExerciseRow
                        key={exercise.key}
                        exercise={exercise}
                        number={numbering.get(levelData.level)?.get(exercise.key)}
                        concepts={levelData.concepts}
                      />
                    ))}
                  </div>
                </div>
              ))
            ) : (
              <div className="divide-y">
                {levelData.exercises.map((exercise, idx) => (
                  <ExerciseRow
                    key={exercise.key}
                    exercise={exercise}
                    number={idx + 1}
                    concepts={levelData.concepts}
                  />
                ))}
              </div>
            )}
          </div>
        );
      })}

    </div>
  );
}

/**
 * The skill's concepts across the top of a level, each with the number of
 * questions practising it and a way to generate more for that one.
 *
 * The point is the thin ones. A level of fifty questions can leave a concept at
 * zero, and until this was on screen there was no way to see it — the level
 * looked full.
 */
function ConceptCoverageBar({
  subject,
  grade,
  skillKey,
  level,
  concepts,
  untaggedCount,
}: {
  subject: string;
  grade: number;
  skillKey: string;
  level: number;
  concepts: SkillConcept[];
  untaggedCount: number;
}) {
  const [generateFiles] = useMutation(GENERATE_EXERCISE_FILES);
  const [busyKey, setBusyKey] = useState<string | null>(null);

  const generateFor = async (concept: SkillConcept) => {
    setBusyKey(concept.key);
    try {
      // Ask for what the concept is short of at THIS level, the same number rake
      // generate:fill_gaps works to. A fixed batch meant a concept at zero and a
      // concept at eighteen got the same six.
      const missing = Math.max(concept.target - concept.exerciseCount, 1);
      const { data } = await generateFiles({
        variables: {
          skillKey,
          subject,
          grade,
          level,
          count: missing,
          conceptKey: concept.key,
        },
      });
      const result = data?.generateExerciseFiles;
      if (result?.success) {
        toast.success(
          `Sukurta ${result.successCount} užduočių („${concept.name}“). Peržiūrėk laukiančiose.`
        );
      } else {
        toast.error(result?.errors?.[0] || "Nepavyko sugeneruoti");
      }
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Nepavyko sugeneruoti");
    } finally {
      setBusyKey(null);
    }
  };

  return (
    <div className="px-4 py-2 border-t bg-background/40 flex flex-wrap items-center gap-2">
      <span className="text-xs text-muted-foreground mr-1">Sąvokos:</span>
      {concepts.map((concept) => (
        <div
          key={concept.key}
          className={`flex items-center gap-1 rounded border px-2 py-0.5 text-xs ${
            // Questions at a level the concept does not declare: either the
            // difficulty call is wrong or these questions sit at the wrong level.
            // Louder than a plain gap, because it is a contradiction rather than
            // a shortfall.
            concept.target === 0 && concept.exerciseCount > 0
              ? "border-orange-500/70 text-orange-700 dark:text-orange-400"
              : concept.target === 0
                ? "opacity-45 text-muted-foreground"
                : concept.exerciseCount === 0 && concept.coverageMeasured
                  ? "border-amber-400/60 text-amber-700 dark:text-amber-400"
                  : "text-muted-foreground"
          }`}
          title={
            [
              concept.description,
              concept.target === 0 && concept.exerciseCount > 0
                ? `${concept.exerciseCount} užduotys šiame lygyje, nors sąvoka numatyta tik ${concept.levels.join(", ")} lygiui`
                : concept.target === 0
                ? `Šis lygis šiai sąvokai nenumatytas (lygiai: ${concept.levels.join(", ")})`
                : `Lygiai: ${concept.levels.join(", ")} · ${priorityLabels[concept.priority] ?? concept.priority}`,
              concept.difficultyDeclared ? null : "Sunkumas dar nenustatytas — rake concepts:levels",
              concept.coverageMeasured ? null : "Užduotys dar nesuskaičiuotos — rake concepts:audit",
            ]
              .filter(Boolean)
              .join(" · ") || undefined
          }
        >
          <span>{concept.name}</span>
          {concept.priority === "high" && concept.target > 0 && (
            <span className="text-amber-600 dark:text-amber-400" title="Svarbi sąvoka">
              ★
            </span>
          )}
          {/* A level the concept does not belong on shows a dash, not 0/20: there
              is nothing to be short of. */}
          <span className="font-mono">
            {concept.target === 0 ? (
              <span className={concept.exerciseCount > 0 ? "" : "opacity-60"}>
                {concept.exerciseCount > 0 ? `${concept.exerciseCount}/—` : "—"}
              </span>
            ) : concept.coverageMeasured ? (
              <>
                {concept.exerciseCount}
                <span className="opacity-50">/{concept.target}</span>
              </>
            ) : (
              /* Nothing has counted this skill's questions per concept, so the
                 count is unknown. A 0 here would read as "nothing practises it"
                 and send someone generating questions that may already exist. */
              <span className="opacity-50">?/{concept.target}</span>
            )}
          </span>
          {concept.theoryBlocks === 0 && (
            <span title="Teorijoje šios sąvokos niekas nepaaiškina">
              <BookOpen className="h-3 w-3 text-amber-600 dark:text-amber-400" />
            </span>
          )}
          <button
            type="button"
            disabled={busyKey !== null}
            onClick={() => generateFor(concept)}
            className="ml-1 hover:text-foreground disabled:opacity-40 cursor-pointer"
            title={`Sugeneruoti trūkstamas užduotis šiai sąvokai (${level} lygis)`}
          >
            {busyKey === concept.key ? (
              <Loader2 className="h-3 w-3 animate-spin" />
            ) : (
              <Plus className="h-3 w-3" />
            )}
          </button>
        </div>
      ))}
      {concepts.every((concept) => concept.theoryBlocks === null) && (
        <span
          className="text-xs text-muted-foreground"
          title="Ši teorija parašyta prieš žymėjimą, todėl nežinome, kurią sąvoką kuri jos dalis paaiškina. Sugeneruok teoriją iš naujo, ir žymės atsiras."
        >
          teorija nepažymėta
        </span>
      )}
      {untaggedCount > 0 && (
        <span
          className="text-xs text-amber-600 dark:text-amber-400"
          title="Šių užduočių teorija neapima — arba papildyk teoriją, arba perkelk jas"
        >
          {untaggedCount} be sąvokos
        </span>
      )}
    </div>
  );
}

// ─── Main Page ───

export default function ExerciseBrowserPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const catalog = useContentCatalog();
  const { filter, setFilter, clearFilter } = useContentFilter();
  const viewMode = searchParams.get("view") || "exercises";
  // Skills with nothing approved are hidden by default and never when one is
  // picked: „every skill in the app" is 650 rows of zeros, and a selection that
  // showed nothing at all would read as a broken filter rather than as an empty
  // skill.
  const showEmpty = searchParams.get("empty") === "1";

  // Two sets rather than one, because a skill can be open for two reasons and
  // they have to be separable: picking one skill in the filter opens it, and the
  // reader must still be able to shut it again.
  const [opened, setOpened] = useState<Set<string>>(new Set());
  const [closed, setClosed] = useState<Set<string>>(new Set());

  const [generateSuggested] = useMutation(GENERATE_SUGGESTED_EXERCISES);
  const [suggesting, setSuggesting] = useState(false);

  const { data: summaryData, loading: summaryLoading, refetch: refetchSummary } =
    useQuery(GET_EXERCISES_SUMMARY);

  // Pending exercise file counts per skill (from content/exercises-pending/)
  const { data: pendingExCountsData, refetch: refetchPendingExCounts } =
    useQuery(GET_PENDING_EXERCISE_COUNTS);

  const pendingExCounts: Record<string, number> =
    pendingExCountsData?.pendingExerciseCounts || {};

  const totalPendingCount = Object.values(pendingExCounts).reduce(
    (sum, c) => sum + c,
    0
  );

  // Pending evaluation review counts per skill (from .eval.json sidecars)
  const { data: pendingCountsData } = useQuery(GET_PENDING_REVIEW_COUNTS);

  const pendingCounts: Record<string, number> =
    pendingCountsData?.pendingReviewCounts || {};

  const summary: Summary | null = summaryData?.exercisesSummary?.summary
    ? typeof summaryData.exercisesSummary.summary === "string"
      ? JSON.parse(summaryData.exercisesSummary.summary)
      : summaryData.exercisesSummary.summary
    : null;

  // How many approved questions each skill has, flattened out of the summary
  // tree. Keyed by skill alone: a skill id is unique across the whole config, and
  // it is what every path, log line and filter uses.
  const countsBySkill = useMemo(() => {
    const counts = new Map<string, SkillInfo["counts"]>();
    Object.values(summary || {}).forEach((subject) =>
      Object.values(subject.grades).forEach((grade) =>
        grade.skills.forEach((skill) => counts.set(skill.key, skill.counts)),
      ),
    );
    return counts;
  }, [summary]);

  const totalOf = (key: string) => {
    const counts = countsBySkill.get(key);
    if (!counts) return 0;
    if (filter.levels.length === 0) {
      return counts.level_1 + counts.level_2 + counts.level_3;
    }
    return filter.levels.reduce(
      (sum, level) => sum + (counts[`level_${level}` as keyof SkillInfo["counts"]] || 0),
      0,
    );
  };

  // The skills on screen come from `config/skills.yml` (the catalog) rather than
  // from the content tree, so a skill nobody has generated for can still be
  // picked and seen to be empty — those are the ones with the most work to do.
  const visibleSkills = useMemo(() => {
    const picked = new Set(filter.skills);
    return filterSkills(catalog, filter).filter(
      (skill) => showEmpty || picked.has(skill.id) || totalOf(skill.id) > 0,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [catalog, filter, showEmpty, countsBySkill]);

  const hiddenEmptyCount = useMemo(
    () =>
      showEmpty
        ? 0
        : filterSkills(catalog, filter).filter(
            (skill) => !filter.skills.includes(skill.id) && totalOf(skill.id) === 0,
          ).length,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [catalog, filter, showEmpty, countsBySkill],
  );

  const totalExercises = visibleSkills.reduce((sum, skill) => sum + totalOf(skill.id), 0);

  // Open a skill picked in the filter — usually somebody has picked the one they
  // came to read — unless they have since shut it.
  const autoOpen = filter.skills.length > 0 && filter.skills.length <= 5;
  const isExpanded = (key: string) =>
    opened.has(key) || (autoOpen && filter.skills.includes(key) && !closed.has(key));

  const toggleSkill = (key: string) => {
    const wasOpen = isExpanded(key);
    setOpened((prev) => {
      const next = new Set(prev);
      wasOpen ? next.delete(key) : next.add(key);
      return next;
    });
    setClosed((prev) => {
      const next = new Set(prev);
      wasOpen ? next.add(key) : next.delete(key);
      return next;
    });
  };

  // `replace` for the settings on this page — a filter is not somewhere you were.
  // Paging through the review queue is: „Atgal" walking back a skill is what a
  // browser is for, and it is the same gesture as the „Ankstesnis" button.
  const setParam = (key: string, value: string, push = false) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (value) next.set(key, value);
      else next.delete(key);
      return next;
    }, { replace: !push });
  };

  // The skills the pending page is showing, so the concept picker can offer
  // theirs — the skill picker that used to scope it is off in that view.
  const [pendingPageSkills, setPendingPageSkills] = useState<string[]>([]);

  const setViewMode = (mode: string) => {
    // Refresh counts when leaving pending view
    if (viewMode === "pending" && mode !== "pending") {
      refetchPendingExCounts();
      refetchSummary();
    }
    setParam("view", mode === "exercises" ? "" : mode);
  };

  // One subject, because that is what the mutation picks a skill inside of. Not
  // offered at all otherwise: a button that has to guess which of two subjects
  // was meant is a button that generates the wrong questions.
  const suggestSubject = filter.subjects.length === 1 ? filter.subjects[0] : undefined;
  const suggestGrade = filter.grades.length === 1 ? filter.grades[0] : undefined;

  const handleGenerateSuggested = async () => {
    if (!suggestSubject) return;

    setSuggesting(true);
    try {
      const { data } = await generateSuggested({
        variables: { subject: suggestSubject, grade: suggestGrade },
      });
      const r = data?.generateSuggestedExercises;
      if (!r) {
        toast.error("Klaida generuojant");
        return;
      }
      if (!r.suggested) {
        toast.info("Viskas užpildyta — nėra ką generuoti");
        return;
      }
      if (r.success) {
        const gradeNote = r.fallbackGrade ? ` (${r.grade} klasė)` : "";
        toast.success(`Sugeneruota: ${r.skillName} · L${r.level}${gradeNote}`, {
          description: r.reason ?? undefined,
        });
        refetchSummary();
        refetchPendingExCounts();
      } else {
        toast.error(r.errors?.join(", ") || "Generavimo klaida");
      }
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : "Klaida");
    } finally {
      setSuggesting(false);
    }
  };

  if (summaryLoading || catalog.loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Filters. Above both views on purpose: a filter set while reviewing drafts
          still holds when you switch to the approved questions, which is how you
          check that a batch landed where it was aimed. */}
      <div className="space-y-3 sticky top-0 z-10 bg-background py-3 border-b">
        <div className="flex gap-4 items-center flex-wrap">
          <div className="flex rounded-md border overflow-hidden">
            <button
              className={`px-3 py-1.5 text-sm font-medium transition-colors ${
                viewMode === "exercises"
                  ? "bg-primary text-primary-foreground"
                  : "hover:bg-muted"
              }`}
              onClick={() => setViewMode("exercises")}
            >
              Patvirtintos
            </button>
            <button
              className={`px-3 py-1.5 text-sm font-medium transition-colors flex items-center gap-1.5 ${
                viewMode === "pending"
                  ? "bg-primary text-primary-foreground"
                  : "hover:bg-muted"
              }`}
              onClick={() => setViewMode("pending")}
            >
              Laukia patvirtinimo
              {totalPendingCount > 0 && (
                <span
                  className={`text-xs rounded-full px-1.5 py-0.5 leading-none font-bold ${
                    viewMode === "pending"
                      ? "bg-primary-foreground text-primary"
                      : "bg-orange-500 text-white"
                  }`}
                >
                  {totalPendingCount}
                </span>
              )}
            </button>
          </div>

          {viewMode === "exercises" && (
            <div className="ml-auto flex items-center gap-3">
              {suggestSubject && (
                <Button
                  size="sm"
                  onClick={handleGenerateSuggested}
                  disabled={suggesting}
                  title="Sugeneruoti reikalingiausias užduotis pagal mokinių pažangą"
                >
                  {suggesting ? (
                    <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                  ) : (
                    <Wand2 className="h-4 w-4 mr-1" />
                  )}
                  Generuoti reikalingas
                </Button>
              )}
              <span className="text-sm text-muted-foreground">
                {visibleSkills.length} įgūd.
              </span>
              <span className="text-sm text-muted-foreground">{totalExercises} užd.</span>
            </div>
          )}
        </div>

        <ContentFilterBar
          catalog={catalog}
          filter={filter}
          setFilter={setFilter}
          clearFilter={clearFilter}
          // The review queue navigates by skill above the page, so a second skill
          // picker here would be two controls doing one thing — and the concepts
          // on offer are the page's own rather than a picked skill's.
          showSkills={viewMode !== "pending"}
          conceptSkills={viewMode === "pending" ? pendingPageSkills : undefined}
        />
      </div>

      {/* Pending exercises view */}
      {viewMode === "pending" && (
        <PendingExercisesView
          filter={filter}
          setFilter={setFilter}
          sort={searchParams.get("sort") === "concept" ? "concept" : "level"}
          onSortChange={(sort) => setParam("sort", sort === "level" ? "" : sort)}
          atSkill={searchParams.get("atSkill") || undefined}
          onAtSkillChange={(skillKey) => setParam("atSkill", skillKey, Boolean(skillKey))}
          onPageSkillsChange={setPendingPageSkills}
          onCountChange={refetchPendingExCounts}
        />
      )}

      {/* Exercises view */}
      {viewMode === "exercises" && (
        <>
          {visibleSkills.map((skill, index) => {
            const expanded = isExpanded(skill.id);
            const counts = countsBySkill.get(skill.id);
            const exerciseCount = totalOf(skill.id);
            const pendingCount =
              (pendingExCounts[skill.id] || 0) + (pendingCounts[skill.id] || 0);
            // A heading whenever the subject or the grade changes: the list spans
            // both now, and „Daugyba" says nothing about which year it belongs to.
            const previous = visibleSkills[index - 1];
            const newGroup =
              !previous || previous.subject !== skill.subject || previous.grade !== skill.grade;

            return (
              <div key={skill.id} className="space-y-0">
                {newGroup && (
                  <div className="flex items-center gap-2 pt-3 pb-1 text-xs font-medium text-muted-foreground">
                    {subjectIcons[skill.subject] || <FileText className="h-4 w-4" />}
                    <span>{skill.subjectName}</span>
                    <span>·</span>
                    <span>{skill.grade} klasė</span>
                  </div>
                )}

                {/* Skill header */}
                <div
                  className={`bg-background border px-4 py-3 flex items-center gap-3 cursor-pointer hover:bg-muted/50 ${
                    expanded ? "rounded-t-lg" : "rounded-lg"
                  }`}
                  onClick={() => toggleSkill(skill.id)}
                >
                  {expanded ? (
                    <ChevronDown className="h-4 w-4 shrink-0" />
                  ) : (
                    <ChevronRight className="h-4 w-4 shrink-0" />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold truncate">{skill.name}</div>
                    <div className="text-xs text-muted-foreground">{skill.id}</div>
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    {pendingCount > 0 && (
                      <Badge className="text-xs bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200 gap-1">
                        <Eye className="h-3 w-3" />
                        {pendingCount} laukia
                      </Badge>
                    )}
                    {[1, 2, 3]
                      .filter((level) => filter.levels.length === 0 || filter.levels.includes(level))
                      .map((level) => (
                        <Badge key={level} className={`text-xs ${levelColors[level]}`}>
                          {counts?.[`level_${level}` as keyof SkillInfo["counts"]] ?? 0}
                        </Badge>
                      ))}
                  </div>
                  <Badge variant="outline" className="text-xs shrink-0">
                    {exerciseCount} užd.
                  </Badge>
                  <Button
                    size="sm"
                    variant="outline"
                    className="shrink-0"
                    onClick={(e) => {
                      e.stopPropagation();
                      const params = new URLSearchParams({
                        skill: skill.id,
                        name: skill.name,
                        subject: skill.subject,
                        grade: String(skill.grade),
                      });
                      // The preview, not the generator: look at what the theory
                      // teaches and what practises it before regenerating it.
                      // „Generuoti teoriją" is one click from there.
                      navigate(`/admin/theory?${params}`);
                    }}
                  >
                    <BookOpen className="h-3.5 w-3.5 mr-1" />
                    Teorija
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="shrink-0"
                    onClick={(e) => {
                      e.stopPropagation();
                      const params = new URLSearchParams({
                        skill: skill.id,
                        name: skill.name,
                        subject: skill.subject,
                        grade: String(skill.grade),
                        ...(filter.levels.length === 1 ? { level: String(filter.levels[0]) } : {}),
                      });
                      navigate(`/admin/exercises/generate?${params}`);
                    }}
                  >
                    <Plus className="h-3.5 w-3.5 mr-1" />
                    Generuoti
                  </Button>
                </div>

                {/* Exercises loaded on demand */}
                {expanded && (
                  <SkillExercises
                    subject={skill.subject}
                    grade={skill.grade}
                    skillKey={skill.id}
                    filter={filter}
                  />
                )}
              </div>
            );
          })}

          {(hiddenEmptyCount > 0 || showEmpty) && (
            <button
              type="button"
              onClick={() => setParam("empty", showEmpty ? "" : "1")}
              className="text-xs text-muted-foreground hover:text-foreground underline"
            >
              {showEmpty
                ? "Slėpti įgūdžius be užduočių"
                : `Rodyti dar ${hiddenEmptyCount} įgūdžius be užduočių`}
            </button>
          )}

          {visibleSkills.length === 0 && (
            <Card>
              <CardContent className="py-12 text-center text-muted-foreground">
                Nerasta įgūdžių pagal pasirinktus filtrus
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  );
}
